/**
 * FlightCalendar.jsx - 機体予約・フライト管理 - Retro-Brutalistic デザインシステム
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import jaLocale from '@fullcalendar/core/locales/ja';
import { flightApi, droneApi, userApi, maintenanceApi } from '../services/droneOpsApi';
import { naturalSortBy } from '../services/utils';
import { Input, Select } from '../components/ui';
import { Textarea } from '../components/ui/Input';

const STATUS_LABELS = {
  planned:   '予約中',
  in_flight: '飛行中',
  completed: '完了',
  cancelled: 'キャンセル',
};

const STATUS_COLORS = {
  planned:   null,
  in_flight: '#10B981',
  completed: '#6B7280',
  cancelled: '#EF4444',
};

const OPERATIONAL_COLORS = {
  available:   '#22c55e',
  in_flight:   '#3b82f6',
  maintenance: '#f59e0b',
  repair:      '#ef4444',
  unknown:     '#64748b',
};

const OPERATIONAL_LABELS = {
  available:   '待機中',
  in_flight:   '飛行中',
  maintenance: '点検中',
  repair:      '修理中',
  unknown:     '不明',
};

const MAINT_TYPE_LABELS = {
  periodic_3m: '3ヶ月定期点検',
  periodic_1m: '1ヶ月定期点検',
  battery:     'バッテリー',
  repair:      '修理',
  pre_flight:  '飛行前点検',
  post_flight: '飛行後点検',
};

// 予約種別（カレンダー色分け用）
const FLIGHT_CATEGORIES = {
  flight:      { label: '飛行',     color: '#0066FF' },
  inspection:  { label: '点検',     color: '#FF6600' },
  training:    { label: '訓練',     color: '#9900FF' },
  demo:        { label: 'デモ',     color: '#00CCFF' },
  survey:      { label: '測量',     color: '#00AA00' },
  spray:       { label: '散布',     color: '#CCCC00' },
  other:       { label: 'その他',   color: '#666666' },
};

const EMPTY_FORM = {
  thingName:  '',
  groupName:  '',
  pilotId:    '',
  pilotName:  '',
  category:   'flight',
  title:      '',
  startTime:  '',
  endTime:    '',
  location:   '',
  purpose:    '',
  notes:      '',
  altitude:   '',
};

// ===== インラインコンポーネント =====

function Spinner() {
  return (
    <svg
      className="animate-spin"
      style={{ width: 40, height: 40, color: '#0068B7' }}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

function QuestWindow({ children, className = '' }) {
  return (
    <div
      className={`relative bg-white border-4 border-[#0068B7] rounded-xl p-6 ${className}`}
      style={{ boxShadow: '0 8px 0 0 #00508e' }}
    >
      <div className="absolute inset-2 border-2 border-[#0068B7]/20 rounded-lg pointer-events-none" />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

function AeroButton({ onClick, disabled, isLoading, children, variant = 'primary', type = 'button', className = '' }) {
  const base = 'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';

  const variants = {
    primary: {
      className: 'bg-[#0068B7] text-white hover:bg-[#0057a0] active:translate-y-0.5',
      style: { fontFamily: 'Space Grotesk, sans-serif', boxShadow: disabled || isLoading ? 'none' : '0 4px 0 0 #00508e' },
    },
    secondary: {
      className: 'bg-white text-[#0068B7] border-2 border-[#0068B7] hover:bg-blue-50 active:translate-y-0.5',
      style: { fontFamily: 'Space Grotesk, sans-serif', boxShadow: disabled || isLoading ? 'none' : '0 4px 0 0 #0068B740' },
    },
    danger: {
      className: 'bg-[#DC2626] text-white hover:bg-[#B91C1C] active:translate-y-0.5',
      style: { fontFamily: 'Space Grotesk, sans-serif', boxShadow: disabled || isLoading ? 'none' : '0 4px 0 0 #991B1B' },
    },
    'danger-outline': {
      className: 'bg-white text-[#DC2626] border-2 border-[#DC2626] hover:bg-red-50 active:translate-y-0.5',
      style: { fontFamily: 'Space Grotesk, sans-serif', boxShadow: disabled || isLoading ? 'none' : '0 4px 0 0 #DC262640' },
    },
  };

  const v = variants[variant] || variants.primary;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`${base} ${v.className} ${className}`}
      style={v.style}
    >
      {isLoading ? (
        <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : null}
      {children}
    </button>
  );
}

function ErrorAlert({ message }) {
  if (!message) return null;
  return (
    <div
      className="flex items-start gap-2 px-4 py-3 rounded-lg border-2 border-[#DC2626] text-sm"
      style={{ backgroundColor: '#FEF2F2', color: '#991B1B', fontFamily: 'Inter, sans-serif' }}
    >
      <span className="material-symbols-outlined flex-shrink-0" style={{ fontSize: '18px', marginTop: '1px' }}>
        error
      </span>
      <span>{message}</span>
    </div>
  );
}

// ===== ミニカレンダー =====
function MiniCalendar({ selectedDate, onDateClick, onMonthChange }) {
  const [viewDate, setViewDate] = useState(selectedDate || new Date());

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDayOfWeek = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  const days = [];
  for (let i = 0; i < startDayOfWeek; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const today = new Date();
  const isToday = (day) =>
    day && year === today.getFullYear() && month === today.getMonth() && day === today.getDate();

  const isSelected = (day) =>
    day && selectedDate && year === selectedDate.getFullYear() && month === selectedDate.getMonth() && day === selectedDate.getDate();

  const prevMonth = () => {
    const newDate = new Date(year, month - 1, 1);
    setViewDate(newDate);
    onMonthChange?.(newDate);
  };

  const nextMonth = () => {
    const newDate = new Date(year, month + 1, 1);
    setViewDate(newDate);
    onMonthChange?.(newDate);
  };

  const handleDayClick = (day) => {
    if (day) {
      onDateClick?.(new Date(year, month, day));
    }
  };

  // 曜日インデックス(0=日, 6=土) から色を返す
  const weekdayColor = (colIdx) => {
    if (colIdx === 0) return '#ba1a1a';   // 日: error
    if (colIdx === 6) return '#267120';   // 土: on-secondary-container
    return '#717782';                     // 平日: outline
  };

  return (
    <div
      className="select-none border-2 rounded-lg p-3"
      style={{ borderColor: 'rgba(0,104,183,0.25)', backgroundColor: '#ffffff', boxShadow: '0 2px 0 0 rgba(0,104,183,0.15)' }}
    >
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={prevMonth}
          className="p-1 rounded transition-colors hover:bg-[#e0e2e9]"
          style={{ color: '#0068B7' }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chevron_left</span>
        </button>
        <span
          style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontWeight: 700,
            fontSize: '13px',
            color: '#191c21',
            letterSpacing: '-0.01em',
          }}
        >
          {year}年{month + 1}月
        </span>
        <button
          onClick={nextMonth}
          className="p-1 rounded transition-colors hover:bg-[#e0e2e9]"
          style={{ color: '#0068B7' }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chevron_right</span>
        </button>
      </div>

      {/* 曜日ヘッダー */}
      <div className="grid grid-cols-7 gap-0 text-center mb-1">
        {['日', '月', '火', '水', '木', '金', '土'].map((d, i) => (
          <div
            key={d}
            className="py-1"
            style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: '9px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: weekdayColor(i),
              textTransform: 'uppercase',
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* 日付グリッド */}
      <div className="grid grid-cols-7 gap-0 text-center">
        {days.map((day, i) => {
          const colIdx = i % 7;
          let textColor;
          if (isToday(day)) {
            textColor = '#ffffff';
          } else if (isSelected(day)) {
            textColor = '#0068B7';
          } else if (colIdx === 0 && day) {
            textColor = '#ba1a1a';
          } else if (colIdx === 6 && day) {
            textColor = '#267120';
          } else {
            textColor = '#414751';
          }

          return (
            <button
              key={i}
              onClick={() => handleDayClick(day)}
              disabled={!day}
              className="w-7 h-7 mx-auto flex items-center justify-center rounded-full transition-all"
              style={{
                fontSize: '11px',
                fontFamily: 'Space Grotesk, sans-serif',
                fontWeight: isToday(day) || isSelected(day) ? 700 : 500,
                backgroundColor: isToday(day)
                  ? '#0068b7'
                  : isSelected(day) && !isToday(day)
                  ? '#d3e4ff'
                  : 'transparent',
                color: textColor,
                cursor: day ? 'pointer' : 'default',
              }}
              onMouseEnter={(e) => {
                if (day && !isToday(day)) e.currentTarget.style.backgroundColor = '#e0e2e9';
              }}
              onMouseLeave={(e) => {
                if (day && !isToday(day)) {
                  e.currentTarget.style.backgroundColor = isSelected(day) ? '#d3e4ff' : 'transparent';
                }
              }}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ===== メインコンポーネント =====
export default function FlightCalendar() {
  const calendarRef = useRef(null);

  const [flights,  setFlights]  = useState([]);
  const [drones,   setDrones]   = useState([]);
  const [groups,   setGroups]   = useState([]);
  const [users,    setUsers]    = useState([]);
  const [alerts,   setAlerts]   = useState([]);

  const [loading,        setLoading]        = useState(true);
  const [showModal,      setShowModal]      = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [form,           setForm]           = useState(EMPTY_FORM);
  const [formError,      setFormError]      = useState('');
  const [submitting,     setSubmitting]     = useState(false);
  const [currentRange,   setCurrentRange]   = useState(null);
  const [groupConfirm,   setGroupConfirm]   = useState(null);
  const [selectedDate,   setSelectedDate]   = useState(new Date());

  const [filterGroup, setFilterGroup] = useState('');
  const [filterThing, setFilterThing] = useState('');

  const [dronesExpanded, setDronesExpanded] = useState(true);
  const [groupsExpanded, setGroupsExpanded] = useState(true);
  const [alertsExpanded, setAlertsExpanded] = useState(false);

  const [visibleThings, setVisibleThings] = useState(new Set());
  const [visibleGroups, setVisibleGroups] = useState(new Set());

  const [sectionOrder, setSectionOrder] = useState(() => {
    const saved = localStorage.getItem('fc-section-order');
    return saved ? JSON.parse(saved) : ['drones', 'groups'];
  });
  const [draggedSection, setDraggedSection] = useState(null);

  const handleSectionDragStart = (e, sectionId) => {
    setDraggedSection(sectionId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', sectionId);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleSectionDragEnd = (e) => {
    setDraggedSection(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleSectionDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSectionDrop = (e, targetSectionId) => {
    e.preventDefault();
    if (!draggedSection || draggedSection === targetSectionId) return;

    const newOrder = [...sectionOrder];
    const draggedIdx = newOrder.indexOf(draggedSection);
    const targetIdx = newOrder.indexOf(targetSectionId);

    newOrder.splice(draggedIdx, 1);
    newOrder.splice(targetIdx, 0, draggedSection);

    setSectionOrder(newOrder);
    localStorage.setItem('fc-section-order', JSON.stringify(newOrder));
  };

  useEffect(() => {
    if (drones.length > 0 && visibleThings.size === 0) {
      setVisibleThings(new Set(drones.map(d => d.thingName)));
    }
  }, [drones]);

  useEffect(() => {
    if (groups.length > 0 && visibleGroups.size === 0) {
      setVisibleGroups(new Set(groups.map(g => g.groupName)));
    }
  }, [groups]);

  const filterGroupObj  = groups.find(g => g.groupName === filterGroup);
  const filteredDrones  = drones;

  const fetchFlights = useCallback(async (from, to) => {
    try {
      const data = await flightApi.list({ from, to });
      setFlights(data.flights ?? []);
    } catch (e) {
      console.error('予約取得エラー:', e);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const [droneData, userData, alertData] = await Promise.all([
          droneApi.list(),
          userApi.list(),
          maintenanceApi.alerts({ days: 7 }),
        ]);
        const droneList = droneData.drones ?? [];
        setDrones(naturalSortBy(droneList, 'thingName'));
        setUsers(naturalSortBy(userData.users ?? [], 'name'));
        setAlerts(alertData.alerts ?? []);

        try {
          const groupData = await droneApi.listGroups();
          setGroups(naturalSortBy(groupData.groups ?? [], 'displayName'));
        } catch {
          const grpSet = new Set();
          droneList.forEach(d => (d.groups ?? []).forEach(g => grpSet.add(g)));
          setGroups([...grpSet].sort((a,b) => a.localeCompare(b, undefined, { numeric: true })).map(g => ({ groupName: g, displayName: g, things: [] })));
        }
      } catch (e) {
        console.error('初期データ取得エラー:', e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (currentRange) fetchFlights(currentRange.from, currentRange.to);
  }, [currentRange, fetchFlights]);

  const droneMap = Object.fromEntries(drones.map(d => [d.thingName, d]));

  const bookedThings = new Set(
    flights
      .filter(f => ['planned', 'in_flight'].includes(f.status))
      .map(f => f.thingName)
  );

  const filteredFlights = flights.filter(f => {
    if (!visibleThings.has(f.thingName)) return false;
    if (filterThing && f.thingName !== filterThing) return false;
    if (filterGroup && !filterGroupObj?.things?.includes(f.thingName)) return false;
    return true;
  });

  const calendarEvents = filteredFlights.map((f) => {
    let color;
    if (f.status === 'cancelled') {
      color = STATUS_COLORS.cancelled;
    } else if (f.status === 'completed') {
      color = STATUS_COLORS.completed;
    } else {
      color = FLIGHT_CATEGORIES[f.category]?.color ?? FLIGHT_CATEGORIES.flight.color;
    }
    return {
      id:              f.flightId,
      title:           f.displayName ?? f.thingName,
      start:           f.startTime,
      end:             f.endTime,
      backgroundColor: color,
      borderColor:     color,
      extendedProps:   f,
    };
  });

  const renderEventContent = (eventInfo) => {
    const f = eventInfo.event.extendedProps;
    const categoryLabel = FLIGHT_CATEGORIES[f.category]?.label ?? '';

    return (
      <div className="fc-event-custom text-white text-xs leading-tight p-1" style={{ fontSize: '11px' }}>
        <div className="font-semibold">{f.displayName ?? f.thingName}</div>
        <div>{categoryLabel} / {f.pilotName || '未設定'}</div>
        {f.location && <div className="opacity-80">{f.location}</div>}
      </div>
    );
  };

  const handleDatesSet = (info) => {
    const from = info.startStr.split('T')[0];
    const to   = info.endStr.split('T')[0];
    setCurrentRange({ from, to });
    fetchFlights(from, to);
  };

  const handleDateSelect = (info) => {
    const toLocal = (date) => {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };
    const start = info.allDay ? `${info.startStr}T09:00` : toLocal(info.start);
    const end   = info.allDay ? `${info.startStr}T10:00` : toLocal(info.end);
    setForm({ ...EMPTY_FORM, startTime: start, endTime: end, groupName: filterGroup, thingName: filterThing });
    setSelectedFlight(null);
    setFormError('');
    setShowModal(true);
  };

  const handleEventClick = (info) => {
    setSelectedFlight(info.event.extendedProps);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowModal(true);
  };

  const handleMiniCalendarDateClick = (date) => {
    setSelectedDate(date);
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.gotoDate(date);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'pilotId') {
        const user = users.find(u => u.userId === value);
        next.pilotName = user?.name ?? user?.email ?? '';
      }
      if (name === 'groupName') next.thingName = '';
      return next;
    });
  };

  const selectedGroupObj = groups.find(g => g.groupName === form.groupName);
  const formDrones       = form.groupName
    ? drones.filter(d => (selectedGroupObj?.things ?? d.groups ?? []).includes(d.thingName)
        || (d.groups ?? []).includes(form.groupName))
    : drones;
  const availableDrones = formDrones.filter(d => d.operationalStatus === 'available');

  const handleSubmit = async () => {
    setFormError('');
    if (!form.pilotId)   return setFormError('パイロットを選択してください');
    if (!form.startTime) return setFormError('開始時刻を入力してください');
    if (!form.endTime)   return setFormError('終了時刻を入力してください');
    if (form.startTime >= form.endTime) return setFormError('終了時刻は開始時刻より後にしてください');

    const isGroupBulk = form.groupName && !form.thingName;
    if (isGroupBulk) {
      const targets = formDrones.filter(d => d.operationalStatus === 'available');
      if (targets.length === 0) return setFormError('グループ内に待機中の機体がありません');
      if (!window.confirm(`グループ「${form.groupName}」の待機中機体 ${targets.length} 台に一括予約します。よろしいですか？`)) return;
    } else if (!form.thingName) {
      return setFormError('機体を選択するか、グループの未選択で一括予約してください');
    }

    setSubmitting(true);
    try {
      if (isGroupBulk) {
        const targets = formDrones.filter(d => d.operationalStatus === 'available');
        const results = await Promise.allSettled(
          targets.map(d => flightApi.create({
            thingName: d.thingName,
            pilotId:   form.pilotId,
            pilotName: form.pilotName,
            title:     form.title,
            startTime: form.startTime,
            endTime:   form.endTime,
            location:  form.location,
            purpose:   form.purpose,
            notes:     form.notes,
            groupName: form.groupName,
            altitude:  form.altitude ? Number(form.altitude) : 0,
            category:  form.category,
          }))
        );
        const failed = results.filter(r => r.status === 'rejected');
        if (failed.length > 0) {
          setFormError(`${targets.length - failed.length}件成功、${failed.length}件失敗（時間帯の重複等）`);
        } else {
          setShowModal(false);
        }
      } else {
        await flightApi.create({
          thingName: form.thingName,
          pilotId:   form.pilotId,
          pilotName: form.pilotName,
          title:     form.title,
          startTime: form.startTime,
          endTime:   form.endTime,
          location:  form.location,
          purpose:   form.purpose,
          notes:     form.notes,
          groupName: form.groupName,
          altitude:  form.altitude ? Number(form.altitude) : 0,
          category:  form.category,
        });
        setShowModal(false);
      }
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) {
      setFormError(
        e.message.includes('重複') || e.message.includes('CONFLICT') || e.message.includes('予約')
          ? 'この時間帯はすでに予約されています'
          : e.message
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (flight) => {
    if (!flight.groupName) {
      if (!window.confirm('この予約をキャンセルしますか？')) return;
      try {
        await flightApi.update(flight.flightId, { status: 'cancelled' });
        setShowModal(false);
        if (currentRange) fetchFlights(currentRange.from, currentRange.to);
      } catch (e) { alert(e.message); }
      return;
    }
    const choice = await showGroupConfirm(flight.groupName, 'キャンセル');
    if (!choice) return;
    try {
      if (choice === 'group') {
        const targets = flights.filter(f =>
          f.groupName === flight.groupName && f.startTime === flight.startTime
        );
        await Promise.allSettled(
          targets.map(f => flightApi.update(f.flightId, { status: 'cancelled' }))
        );
      } else {
        await flightApi.update(flight.flightId, { status: 'cancelled' });
      }
      setShowModal(false);
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) { alert(e.message); }
  };

  const handleDelete = async (flight) => {
    if (!flight.groupName) {
      if (!window.confirm('この予約を削除しますか？')) return;
      try {
        await flightApi.delete(flight.flightId);
        setShowModal(false);
        if (currentRange) fetchFlights(currentRange.from, currentRange.to);
      } catch (e) { alert(e.message); }
      return;
    }
    const choice = await showGroupConfirm(flight.groupName, '削除');
    if (!choice) return;
    try {
      if (choice === 'group') {
        const targets = flights.filter(f =>
          f.groupName === flight.groupName && f.startTime === flight.startTime
        );
        await Promise.allSettled(
          targets.map(f => flightApi.delete(f.flightId))
        );
      } else {
        await flightApi.delete(flight.flightId);
      }
      setShowModal(false);
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) { alert(e.message); }
  };

  const showGroupConfirm = (groupName, action) => {
    return new Promise((resolve) => {
      setGroupConfirm({ groupName, action, resolve });
    });
  };

  const handleEventDrop = async (info) => {
    const flight = info.event.extendedProps;
    const toLocal = (date) => {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    const newStart = toLocal(info.event.start);
    const oldStart = flight.startTime.slice(0, 16);
    const diffMs   = new Date(newStart) - new Date(oldStart);

    const oldDurationMs = new Date(flight.endTime) - new Date(flight.startTime);
    const newEnd = info.event.end
      ? toLocal(info.event.end)
      : toLocal(new Date(new Date(newStart).getTime() + oldDurationMs));

    try {
      if (flight.groupName) {
        const targets = flights.filter(f =>
          f.groupName === flight.groupName &&
          f.startTime.slice(0, 16) === oldStart
        );
        if (targets.length === 0) {
          await flightApi.update(flight.flightId, { startTime: newStart, endTime: newEnd });
        } else {
          await Promise.allSettled(
            targets.map(f => {
              const s = toLocal(new Date(new Date(f.startTime).getTime() + diffMs));
              const dur = new Date(f.endTime) - new Date(f.startTime);
              const e = toLocal(new Date(new Date(s).getTime() + dur));
              return flightApi.update(f.flightId, { startTime: s, endTime: e });
            })
          );
        }
      } else {
        await flightApi.update(flight.flightId, { startTime: newStart, endTime: newEnd });
      }
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) {
      info.revert();
      alert(e.message);
    }
  };

  const handleEventResize = async (info) => {
    const flight = info.event.extendedProps;
    const toLocal = (date) => {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };
    const newEnd = toLocal(info.event.end);
    try {
      if (flight.groupName) {
        const oldEnd = flight.endTime.slice(0, 16);
        const diffMs = new Date(newEnd) - new Date(oldEnd);
        const targets = flights.filter(f =>
          f.groupName === flight.groupName &&
          f.startTime.slice(0, 16) === flight.startTime.slice(0, 16)
        );
        await Promise.allSettled(
          targets.map(f => {
            const e = toLocal(new Date(new Date(f.endTime).getTime() + diffMs));
            return flightApi.update(f.flightId, { endTime: e });
          })
        );
      } else {
        await flightApi.update(flight.flightId, { endTime: newEnd });
      }
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) {
      info.revert();
      alert(e.message);
    }
  };

  const handleUpdate = async (flight, updatedForm, choiceOverride) => {
    const payload = {
      pilotId:   updatedForm.pilotId,
      pilotName: updatedForm.pilotName,
      title:     updatedForm.title,
      startTime: updatedForm.startTime,
      endTime:   updatedForm.endTime,
      location:  updatedForm.location,
      purpose:   updatedForm.purpose,
      notes:     updatedForm.notes,
      altitude:  updatedForm.altitude ? Number(updatedForm.altitude) : 0,
      category:  updatedForm.category,
    };

    let choice = choiceOverride;
    if (flight.groupName && choice === null) {
      choice = await new Promise(resolve => {
        setTimeout(() => {
          setGroupConfirm({ groupName: flight.groupName, action: '変更', resolve });
        }, 0);
      });
      if (!choice) return;
    }

    if (choice === 'single') {
      payload.groupName = '';
      await flightApi.update(flight.flightId, payload);
    } else if (choice === 'group') {
      const targets = flights.filter(f =>
        f.groupName === flight.groupName && f.startTime === flight.startTime
      );
      await Promise.allSettled(
        targets.map(f => flightApi.update(f.flightId, payload))
      );
    } else {
      await flightApi.update(flight.flightId, payload);
    }
    setShowModal(false);
    if (currentRange) fetchFlights(currentRange.from, currentRange.to);
  };

  const toggleThingVisibility = (thingName) => {
    setVisibleThings(prev => {
      const next = new Set(prev);
      if (next.has(thingName)) {
        next.delete(thingName);
      } else {
        next.add(thingName);
      }
      return next;
    });
  };

  const toggleAllThings = () => {
    if (visibleThings.size === drones.length) {
      setVisibleThings(new Set());
    } else {
      setVisibleThings(new Set(drones.map(d => d.thingName)));
    }
  };

  // ===== ローディング =====
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <QuestWindow className="text-center">
          <div className="flex flex-col items-center gap-4">
            <Spinner />
            <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600, color: '#414751' }}>
              読み込み中...
            </p>
          </div>
        </QuestWindow>
      </div>
    );
  }

  return (
    <div className="flex flex-col -m-6 h-[calc(100vh-64px)]">

      {/* ===== ページヘッダー ===== */}
      <div
        className="px-6 pt-5 pb-4 flex-shrink-0 flex items-center gap-3"
        style={{ backgroundColor: 'rgba(255,255,255,0.7)', borderBottom: '2px solid rgba(0,104,183,0.15)' }}
      >
        <span
          className="material-symbols-outlined text-[#0068B7]"
          style={{ fontSize: '28px', fontVariationSettings: "'FILL' 1" }}
        >
          calendar_month
        </span>
        <div>
          <h1
            className="text-xl leading-none text-[#0068B7]"
            style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 800, letterSpacing: '-0.02em' }}
          >
            フライト予約
          </h1>
          <p
            className="mt-0.5 text-xs tracking-widest uppercase"
            style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600, color: '#717782' }}
          >
            Flight Reservation Management
          </p>
        </div>
      </div>

      {/* ===== メインコンテンツ（2カラム） ===== */}
      <div className="flex flex-1 overflow-hidden">

        {/* ===== 左サイドバー ===== */}
        <div
          className="w-56 flex-shrink-0 bg-white overflow-y-auto flex flex-col"
          style={{ borderRight: '4px solid #0068B7' }}
        >
          {/* ミニカレンダー */}
          <div className="px-2 pt-3 pb-2 flex-shrink-0">
            <MiniCalendar
              selectedDate={selectedDate}
              onDateClick={handleMiniCalendarDateClick}
            />
          </div>

          {/* 区切り */}
          <div className="mx-3 border-t-2 border-dashed border-[#0068B7]/20" />

          {/* 機体・グループフィルター */}
          <div className="flex-1">
            {sectionOrder.map(sectionId => {
              if (sectionId === 'drones') {
                return (
                  <div
                    key="drones"
                    className="py-2 cursor-move"
                    style={{ borderTop: '1px solid rgba(0,104,183,0.1)' }}
                    draggable
                    onDragStart={(e) => handleSectionDragStart(e, 'drones')}
                    onDragEnd={handleSectionDragEnd}
                    onDragOver={handleSectionDragOver}
                    onDrop={(e) => handleSectionDrop(e, 'drones')}
                  >
                    {/* セクションヘッダー */}
                    <button
                      onClick={() => setDronesExpanded(!dronesExpanded)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-blue-50 transition-colors group"
                    >
                      <span className="material-symbols-outlined text-[#717782] group-hover:text-[#0068B7] transition-colors" style={{ fontSize: '16px' }}>
                        drag_indicator
                      </span>
                      <span className="material-symbols-outlined text-[#0068B7]" style={{ fontSize: '16px' }}>
                        {dronesExpanded ? 'expand_more' : 'chevron_right'}
                      </span>
                      <span
                        className="text-xs tracking-widest uppercase flex-1"
                        style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, color: '#414751' }}
                      >
                        機体
                      </span>
                      <span
                        className="text-xs px-1.5 py-0.5 rounded-full border"
                        style={{ fontFamily: 'Inter, sans-serif', color: '#717782', borderColor: '#0068B730', backgroundColor: '#0068B710' }}
                      >
                        {drones.length}
                      </span>
                    </button>

                    {dronesExpanded && (
                      <div className="mt-1">
                        {/* 全選択/解除 */}
                        <button
                          onClick={toggleAllThings}
                          className="w-full flex items-center gap-2 px-3 py-1 text-left hover:bg-blue-50 transition-colors"
                        >
                          <span
                            className="w-3.5 h-3.5 rounded-sm border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                            style={{
                              backgroundColor: visibleThings.size === drones.length ? '#0068B7' : 'transparent',
                              borderColor: '#0068B7',
                            }}
                          >
                            {visibleThings.size === drones.length && (
                              <span className="material-symbols-outlined text-white" style={{ fontSize: '10px' }}>check</span>
                            )}
                          </span>
                          <span className="text-xs text-[#717782]" style={{ fontFamily: 'Inter, sans-serif' }}>
                            すべて選択
                          </span>
                        </button>

                        {/* 機体リスト */}
                        <div className="max-h-72 overflow-y-auto">
                          {filteredDrones.map(d => (
                            <button
                              key={d.thingName}
                              onClick={() => toggleThingVisibility(d.thingName)}
                              className="w-full flex items-center gap-2 px-3 py-1 text-left hover:bg-blue-50 transition-colors group"
                            >
                              <span
                                className="w-3.5 h-3.5 rounded-sm border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                                style={{
                                  backgroundColor: visibleThings.has(d.thingName)
                                    ? (d.color || OPERATIONAL_COLORS[d.operationalStatus] || '#0068B7')
                                    : 'transparent',
                                  borderColor: d.color || OPERATIONAL_COLORS[d.operationalStatus] || '#0068B7',
                                }}
                              >
                                {visibleThings.has(d.thingName) && (
                                  <span className="material-symbols-outlined text-white" style={{ fontSize: '10px' }}>check</span>
                                )}
                              </span>
                              <span className="text-sm text-[#414751] truncate flex-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                                {d.displayName}
                              </span>
                              {bookedThings.has(d.thingName) && (
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#0068B7' }} />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              if (sectionId === 'groups' && groups.length > 0) {
                return (
                  <div
                    key="groups"
                    className="py-2 cursor-move"
                    style={{ borderTop: '1px solid rgba(0,104,183,0.1)' }}
                    draggable
                    onDragStart={(e) => handleSectionDragStart(e, 'groups')}
                    onDragEnd={handleSectionDragEnd}
                    onDragOver={handleSectionDragOver}
                    onDrop={(e) => handleSectionDrop(e, 'groups')}
                  >
                    <button
                      onClick={() => setGroupsExpanded(!groupsExpanded)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-blue-50 transition-colors group"
                    >
                      <span className="material-symbols-outlined text-[#717782] group-hover:text-[#0068B7] transition-colors" style={{ fontSize: '16px' }}>
                        drag_indicator
                      </span>
                      <span className="material-symbols-outlined text-[#0068B7]" style={{ fontSize: '16px' }}>
                        {groupsExpanded ? 'expand_more' : 'chevron_right'}
                      </span>
                      <span
                        className="text-xs tracking-widest uppercase flex-1"
                        style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, color: '#414751' }}
                      >
                        グループ
                      </span>
                      <span
                        className="text-xs px-1.5 py-0.5 rounded-full border"
                        style={{ fontFamily: 'Inter, sans-serif', color: '#717782', borderColor: '#0068B730', backgroundColor: '#0068B710' }}
                      >
                        {groups.length}
                      </span>
                    </button>

                    {groupsExpanded && (
                      <div className="mt-1 max-h-72 overflow-y-auto">
                        {groups.map(g => (
                          <button
                            key={g.groupName}
                            onClick={() => {
                              if (filterGroup === g.groupName) {
                                setFilterGroup('');
                              } else {
                                setFilterGroup(g.groupName);
                              }
                            }}
                            className="w-full flex items-center gap-2 px-3 py-1 text-left transition-colors hover:bg-blue-50"
                            style={{ backgroundColor: filterGroup === g.groupName ? '#DBEAFE' : 'transparent' }}
                          >
                            <span className="material-symbols-outlined text-[#717782]" style={{ fontSize: '15px' }}>folder</span>
                            <span className="text-sm text-[#414751] truncate flex-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                              {g.displayName}
                            </span>
                            <span className="text-xs text-[#717782]" style={{ fontFamily: 'Inter, sans-serif' }}>
                              {(g.things ?? []).length}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return null;
            })}

            {/* アラート（点検予定） */}
            {alerts.length > 0 && (
              <div className="py-2" style={{ borderTop: '1px solid rgba(0,104,183,0.1)' }}>
                <button
                  onClick={() => setAlertsExpanded(!alertsExpanded)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-blue-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-[#0068B7]" style={{ fontSize: '16px' }}>
                    {alertsExpanded ? 'expand_more' : 'chevron_right'}
                  </span>
                  <span className="material-symbols-outlined text-amber-500" style={{ fontSize: '16px' }}>warning</span>
                  <span
                    className="text-xs tracking-widest uppercase flex-1"
                    style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, color: '#414751' }}
                  >
                    点検予定
                  </span>
                  <span
                    className="text-xs px-1.5 py-0.5 rounded-full border"
                    style={{ fontFamily: 'Inter, sans-serif', color: '#92400E', borderColor: '#FCD34D', backgroundColor: '#FEF3C7' }}
                  >
                    {alerts.length}
                  </span>
                </button>

                {alertsExpanded && (
                  <div className="mt-1 px-3 space-y-1 max-h-48 overflow-y-auto">
                    {alerts.map(a => (
                      <div key={a.maintId} className="text-xs py-1">
                        <div className="font-medium text-[#414751] truncate" style={{ fontFamily: 'Inter, sans-serif' }}>
                          {a.displayName ?? a.thingName}
                        </div>
                        <div className="text-[#717782]" style={{ fontFamily: 'Inter, sans-serif' }}>{a.scheduledDate}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ===== メインカレンダー ===== */}
        {/* カレンダー列: サイドバーは overflow-hidden で固定、この列だけ横スクロール */}
        <div className="flex-1 min-w-0 overflow-y-auto overflow-x-auto">
          {/* 最小幅ラッパー: 640px 未満になると横スクロールバーが出現 */}
          <div className="h-full min-w-[640px] flex flex-col p-3 pl-0">

            {/* QuestWindow ラッパー */}
            <div
              className="flex-1 min-h-[300px] overflow-hidden border-4 border-[#0068B7] rounded-xl bg-white flex flex-col relative"
              style={{ boxShadow: '0 8px 0 0 #00508e' }}
            >
              {/* 内側インセット線 */}
              <div className="absolute inset-2 border-2 border-[#0068B7]/20 rounded-lg pointer-events-none z-10" />

              <div className="flex-1 p-4 pb-0 overflow-hidden relative z-0">
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                  initialView="timeGridWeek"
                  locale={jaLocale}
                  headerToolbar={{
                    left:   'prev,next today',
                    center: 'title',
                    right:  'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
                  }}
                  buttonText={{ today: '今日', month: '月', week: '週', day: '日', list: 'リスト' }}
                  height="100%"
                  slotMinTime="06:00:00"
                  slotMaxTime="21:00:00"
                  slotDuration="00:30:00"
                  events={calendarEvents}
                  selectable={true}
                  selectMirror={true}
                  select={handleDateSelect}
                  eventClick={handleEventClick}
                  datesSet={handleDatesSet}
                  editable={true}
                  eventDrop={handleEventDrop}
                  eventResize={handleEventResize}
                  eventDisplay="block"
                  eventContent={renderEventContent}
                  nowIndicator={true}
                />
              </div>

              {/* 凡例 */}
              <div
                className="flex flex-wrap gap-3 px-4 py-2.5 flex-shrink-0 relative z-0"
                style={{ borderTop: '2px dashed #c1c7d3' }}
              >
                {Object.entries(FLIGHT_CATEGORIES).map(([key, { label, color }]) => (
                  <div key={key} className="flex items-center gap-1.5">
                    <span
                      className="w-3 h-3 rounded-sm border"
                      style={{ backgroundColor: color, borderColor: 'rgba(0,0,0,0.15)' }}
                    />
                    <span
                      className="text-xs"
                      style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600, color: '#414751', letterSpacing: '0.04em' }}
                    >
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>{/* /QuestWindow */}

          </div>{/* /最小幅ラッパー */}
        </div>{/* /カレンダー列 */}

        {/* ===== グループ確認モーダル ===== */}
        {!!groupConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="w-full max-w-sm">
              <QuestWindow>
                {/* タイトル */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="material-symbols-outlined text-[#0068B7]" style={{ fontSize: '22px' }}>
                    group
                  </span>
                  <h2
                    className="text-lg text-[#0068B7]"
                    style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 800 }}
                  >
                    対象を選択
                  </h2>
                </div>

                <div className="space-y-2 mb-6">
                  <p className="text-sm text-[#414751]" style={{ fontFamily: 'Inter, sans-serif' }}>
                    「{groupConfirm.groupName}」グループで登録された予約です。
                  </p>
                  <p className="text-sm text-[#414751]" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {groupConfirm.action}する対象を選択してください。
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <AeroButton
                    variant="secondary"
                    onClick={() => { setGroupConfirm(null); groupConfirm?.resolve('single'); }}
                  >
                    この機体のみ
                  </AeroButton>
                  <AeroButton
                    variant="danger"
                    onClick={() => { setGroupConfirm(null); groupConfirm?.resolve('group'); }}
                  >
                    グループ全体
                  </AeroButton>
                  <AeroButton
                    variant="secondary"
                    onClick={() => { setGroupConfirm(null); groupConfirm?.resolve(null); }}
                  >
                    閉じる
                  </AeroButton>
                </div>
              </QuestWindow>
            </div>
          </div>
        )}

        {/* ===== 予約モーダル ===== */}
        {showModal && (
          <div
            className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-20 pb-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
          >
            <div
              className="w-full max-w-lg max-h-[calc(100vh-96px)] overflow-y-auto bg-white border-4 border-[#0068B7] rounded-xl"
              style={{ boxShadow: '0 8px 0 0 #00508e' }}
            >
              {/* モーダルヘッダー */}
              <div
                className="flex items-center justify-between px-6 py-4 border-b-2 border-[#0068B7]/20 sticky top-0 bg-white z-10"
              >
                <div className="flex items-center gap-2">
                  {selectedFlight ? (
                    <>
                      <span
                        className="w-3 h-3 rounded-full border border-black/10"
                        style={{ background: selectedFlight.droneColor ?? droneMap[selectedFlight.thingName]?.color ?? '#0068B7' }}
                      />
                      <span
                        className="text-base text-[#0068B7]"
                        style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 800 }}
                      >
                        {selectedFlight.displayName ?? selectedFlight.thingName}
                      </span>
                      {selectedFlight.groupName && (
                        <span
                          className="text-xs px-2 py-0.5 rounded-full border-2 border-[#0068B7] text-[#0068B7]"
                          style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700 }}
                        >
                          {selectedFlight.groupName}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-[#0068B7]" style={{ fontSize: '20px', fontVariationSettings: "'FILL' 1" }}>
                        add_circle
                      </span>
                      <span
                        className="text-base text-[#0068B7]"
                        style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 800 }}
                      >
                        新規予約
                      </span>
                    </>
                  )}
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1.5 rounded-full text-[#717782] hover:bg-blue-50 hover:text-[#0068B7] transition-all"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>close</span>
                </button>
              </div>

              {/* モーダルボディ */}
              <div className="p-6">
                {selectedFlight ? (
                  <FlightDetail
                    flight={selectedFlight}
                    droneMap={droneMap}
                    users={users}
                    onCancel={handleCancel}
                    onDelete={handleDelete}
                    onUpdate={handleUpdate}
                    onClose={() => setShowModal(false)}
                  />
                ) : (
                  <FlightForm
                    form={form}
                    groups={groups}
                    availableDrones={availableDrones}
                    allDrones={formDrones}
                    users={users}
                    error={formError}
                    submitting={submitting}
                    onChange={handleFormChange}
                    onSubmit={handleSubmit}
                    onClose={() => setShowModal(false)}
                  />
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ===== フライト詳細コンポーネント =====
function FlightDetail({ flight, droneMap, users, onCancel, onDelete, onUpdate, onClose }) {
  const [form, setForm] = React.useState({
    pilotId:   flight.pilotId   ?? '',
    pilotName: flight.pilotName ?? '',
    title:     flight.title     ?? '',
    startTime: flight.startTime ? flight.startTime.slice(0, 16) : '',
    endTime:   flight.endTime   ? flight.endTime.slice(0, 16)   : '',
    location:  flight.location  ?? '',
    purpose:   flight.purpose   ?? '',
    notes:     flight.notes     ?? '',
    altitude:  flight.altitude  ?? '',
    category:  flight.category  ?? 'flight',
  });
  const [error,      setError]      = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const isDirty = JSON.stringify(form) !== JSON.stringify({
    pilotId:   flight.pilotId   ?? '',
    pilotName: flight.pilotName ?? '',
    title:     flight.title     ?? '',
    startTime: flight.startTime ? flight.startTime.slice(0, 16) : '',
    endTime:   flight.endTime   ? flight.endTime.slice(0, 16)   : '',
    location:  flight.location  ?? '',
    purpose:   flight.purpose   ?? '',
    notes:     flight.notes     ?? '',
    altitude:  flight.altitude  ?? '',
    category:  flight.category  ?? 'flight',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'pilotId') {
        const user = users.find(u => u.userId === value);
        next.pilotName = user?.name ?? user?.email ?? '';
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.pilotId)   return setError('パイロットを選択してください');
    if (!form.startTime) return setError('開始時刻を入力してください');
    if (!form.endTime)   return setError('終了時刻を入力してください');
    if (form.startTime >= form.endTime) return setError('終了時刻は開始時刻より後にしてください');
    setSubmitting(true);
    try {
      await onUpdate(flight, form, flight.groupName ? null : 'single');
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const STATUS_STYLE = {
    planned:   { bg: '#DBEAFE', color: '#1D4ED8', border: '#93C5FD', label: '予約中' },
    in_flight: { bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7', label: '飛行中' },
    completed: { bg: '#F3F4F6', color: '#374151', border: '#D1D5DB', label: '完了' },
    cancelled: { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5', label: 'キャンセル' },
  };
  const ss = STATUS_STYLE[flight.status] ?? STATUS_STYLE.completed;

  return (
    <div className="space-y-4">
      {error && <ErrorAlert message={error} />}

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Input
            label="タイトル（任意）"
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="例: 現場点検フライト"
          />
        </div>

        <Select
          label="パイロット"
          name="pilotId"
          value={form.pilotId}
          onChange={handleChange}
          options={[
            { value: '', label: '選択してください' },
            ...users.map(u => ({ value: u.userId, label: u.name ?? u.email }))
          ]}
        />

        <div className="leading-normal">
          <label className="block text-sm font-medium text-slate-700 mb-1">ステータス</label>
          <span
            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border-2"
            style={{
              backgroundColor: ss.bg,
              color: ss.color,
              borderColor: ss.border,
              fontFamily: 'Space Grotesk, sans-serif',
            }}
          >
            {ss.label}
          </span>
        </div>

        <Select
          label="予約種別"
          name="category"
          value={form.category}
          onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
          options={Object.entries(FLIGHT_CATEGORIES).map(([key, { label }]) => ({
            value: key,
            label: label
          }))}
        />

        <Input
          label="開始日時"
          type="datetime-local"
          name="startTime"
          value={form.startTime}
          onChange={handleChange}
        />

        <Input
          label="終了日時"
          type="datetime-local"
          name="endTime"
          value={form.endTime}
          onChange={handleChange}
        />

        <Input
          label="場所"
          name="location"
          value={form.location}
          onChange={handleChange}
          placeholder="例: 東京都港区海岸"
        />

        <Input
          label="最大高度 (m)"
          type="number"
          name="altitude"
          value={form.altitude}
          onChange={handleChange}
          placeholder="例: 150"
        />

        <div className="col-span-2">
          <Input
            label="目的"
            name="purpose"
            value={form.purpose}
            onChange={handleChange}
            placeholder="例: 測量・点検・農薬散布"
          />
        </div>

        <div className="col-span-2">
          <Textarea
            label="備考"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            rows={3}
          />
        </div>
      </div>

      <div
        className="flex justify-between items-center pt-4"
        style={{ borderTop: '2px solid rgba(0,104,183,0.15)' }}
      >
        <div className="flex gap-2">
          {flight.status === 'planned' && (
            <AeroButton variant="danger-outline" onClick={() => onCancel(flight)} disabled={submitting}>
              キャンセル
            </AeroButton>
          )}
          {['planned', 'cancelled'].includes(flight.status) && (
            <AeroButton variant="danger" onClick={() => onDelete(flight)} disabled={submitting}>
              <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>delete</span>
              削除
            </AeroButton>
          )}
        </div>
        <div className="flex gap-2">
          <AeroButton variant="secondary" onClick={onClose} disabled={submitting}>
            閉じる
          </AeroButton>
          {isDirty && (
            <AeroButton variant="primary" onClick={handleSave} isLoading={submitting}>
              変更を保存
            </AeroButton>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== 新規予約フォームコンポーネント =====
function FlightForm({ form, groups, availableDrones, allDrones, users, error, submitting, onChange, onSubmit, onClose }) {
  const [showDetails, setShowDetails] = React.useState(false);

  return (
    <div className="space-y-4">
      {error && <ErrorAlert message={error} />}

      {/* 基本項目 */}
      <div className="space-y-4">
        {groups.length > 0 && (
          <Select
            label="グループ（一括予約用）"
            name="groupName"
            value={form.groupName}
            onChange={onChange}
            options={[
              { value: '', label: '指定なし' },
              ...groups.map(g => ({ value: g.groupName, label: g.displayName }))
            ]}
          />
        )}

        <Select
          label="機体"
          name="thingName"
          value={form.thingName}
          onChange={onChange}
          options={[
            { value: '', label: '選択してください' },
            ...(availableDrones.length > 0
              ? [
                  { value: '', label: '── 待機中 ──', disabled: true },
                  ...availableDrones.map(d => ({ value: d.thingName, label: d.displayName })),
                  ...(allDrones.filter(d => d.operationalStatus !== 'available').length > 0
                    ? [
                        { value: '', label: '── 点検中・修理中 ──', disabled: true },
                        ...allDrones.filter(d => d.operationalStatus !== 'available').map(d => ({
                          value: d.thingName,
                          label: `${d.displayName}（${d.operationalStatus === 'maintenance' ? '点検中' : '修理中'}）`,
                          disabled: true
                        }))
                      ]
                    : [])
                ]
              : allDrones.map(d => ({ value: d.thingName, label: d.displayName })))
          ]}
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="開始日時"
            type="datetime-local"
            name="startTime"
            value={form.startTime}
            onChange={onChange}
          />
          <Input
            label="終了日時"
            type="datetime-local"
            name="endTime"
            value={form.endTime}
            onChange={onChange}
          />
        </div>

        <Select
          label="パイロット"
          name="pilotId"
          value={form.pilotId}
          onChange={onChange}
          options={[
            { value: '', label: '選択してください' },
            ...users.map(u => ({ value: u.userId, label: u.name ?? u.email }))
          ]}
        />

        <Select
          label="予約種別"
          name="category"
          value={form.category}
          onChange={onChange}
          options={Object.entries(FLIGHT_CATEGORIES).map(([key, { label }]) => ({
            value: key,
            label: label
          }))}
        />
      </div>

      {/* 詳細オプション（折りたたみ） */}
      <div style={{ borderTop: '2px dashed rgba(0,104,183,0.2)', paddingTop: '12px' }}>
        <button
          type="button"
          onClick={() => setShowDetails(!showDetails)}
          className="flex items-center gap-2 text-sm text-[#0068B7] hover:text-[#00508e] transition-colors"
          style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600 }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
            {showDetails ? 'expand_more' : 'chevron_right'}
          </span>
          詳細オプション
        </button>

        {showDetails && (
          <div className="mt-3 space-y-3">
            <Input
              label="タイトル"
              name="title"
              value={form.title}
              onChange={onChange}
              placeholder="例: 現場点検フライト"
            />

            <div className="grid grid-cols-2 gap-3">
              <Input
                label="場所"
                name="location"
                value={form.location}
                onChange={onChange}
                placeholder="例: 東京都港区海岸"
              />
              <Input
                label="最大高度 (m)"
                type="number"
                name="altitude"
                value={form.altitude}
                onChange={onChange}
                placeholder="例: 150"
              />
            </div>

            <Input
              label="目的"
              name="purpose"
              value={form.purpose}
              onChange={onChange}
              placeholder="例: 測量・点検・農薬散布"
            />

            <Textarea
              label="備考"
              name="notes"
              value={form.notes}
              onChange={onChange}
              rows={2}
              placeholder="特記事項があれば入力"
            />
          </div>
        )}
      </div>

      {/* ボタン */}
      <div
        className="flex justify-end gap-3 pt-3"
        style={{ borderTop: '2px solid rgba(0,104,183,0.15)' }}
      >
        <AeroButton variant="secondary" onClick={onClose}>
          キャンセル
        </AeroButton>
        <AeroButton variant="primary" onClick={onSubmit} isLoading={submitting}>
          予約登録
        </AeroButton>
      </div>
    </div>
  );
}
